import { clsx } from 'clsx'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'
import { useHydrated } from 'remix-utils/use-hydrated'

import { TableOfContents } from './TableOfContents'
import { BreadcrumbBar } from '~/portal/components/ConsumerBreadcrumbBar'
import { EditContentButtons } from '~/portal/components/ConsumerEditContentButtons'
import { Prose } from '~/portal/components/ProseWrapper'

interface LayoutProps {
  children: any
  title?: string
  tableOfContents: any[]
  lastModifiedDate?: string
  providerContentSlug?: string
  showEditButtons?: boolean
  useMarkdocRenderer?: boolean
  isConsumerSide?: boolean
}

export function Layout({
  children,
  title,
  tableOfContents,
  lastModifiedDate,
  providerContentSlug,
  showEditButtons = false,
  useMarkdocRenderer = false,
  isConsumerSide = false,
}: LayoutProps) {
  const { t } = useTranslation()
  const location = useLocation()

  const isHydrated = useHydrated()

  return (
    <div className={clsx('sp-doc-layout relative flex justify-center h-full px-6', !useMarkdocRenderer && 'md:mx-auto')} id="top">
      <div className={clsx(
        'sp-doc-content flex-auto min-w-0 pt-0 xl:pt-6',
        useMarkdocRenderer ? '2xl:max-w-245 xl:max-w-227.5 md:max-w-175' : 'md:max-w-175 xl:mr-4 xl:max-2xl:max-w-165 2xl:mr-8',
      )}>
        {isConsumerSide ? <BreadcrumbBar key={location.pathname} /> : null}
        <article id="syntax-renderer" className="sp-markdown-content pt-6 md:pt-9 xl:pt-6">
          <header className="gap-1 mb-6">
            {isHydrated && lastModifiedDate && (
              <div className="sp-doc-last-modified pb-1 font-semibold leading-5 text-body3 text-(--sp-doc-last-modified-color,var(--neutral-60)) dark:text-(--sp-doc-last-modified-color,var(--neutral-60))">
                {t('consumer.product.template.lastModified', { lastModifiedDate })}
              </div>
            )}
            <div className="flex items-center justify-between pb-5 mt-1 mb-1">
              {title && (
                <div className="text-h1 leading-10.5 tracking-tight font-display text-(--sp-color-text-strong) -ml-0.5">
                  {title}
                </div>
              )}
              {showEditButtons && (
                <div className="sm:hidden xl:block">
                  <EditContentButtons contentUrl={providerContentSlug} />
                </div>
              )}
            </div>
            <hr className="sp-doc-divider h-px border-0 bg-(--sp-color-border-subtle)" />
          </header>
          <Prose useMarkdocRenderer={useMarkdocRenderer}>{children}</Prose>
        </article>
      </div>
      {!useMarkdocRenderer ? <TableOfContents tableOfContents={tableOfContents} /> : null}
    </div>
  )
}